/**
 * DeliveryDetailPage — `/delivery/items/:id` route detail with stops list.
 *
 * "Item" in the URL is the route id — service-delivery exposes routes (with
 * stops) and PODs but doesn't yet have a single-shipment detail surface
 * separate from `GET /v1/routes/:id`. This page renders the route header +
 * stops timeline; per-mail-item drilldown is already handled by `/mail/items/:id`.
 */
import type { JSX } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import { getRoute } from '../../lib/api/delivery';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';

export function DeliveryDetailPage(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const route = useQuery({
    queryKey: ['delivery', 'routes', 'detail', id],
    queryFn: () => getRoute(id),
    retry: false,
    enabled: id !== '',
  });

  const denied =
    route.isError && route.error instanceof ApiError && (route.error.status === 401 || route.error.status === 403);
  const notFound =
    route.isError && route.error instanceof ApiError && route.error.status === 404;

  return (
    <div className="space-y-6">
      <PageHeader
        title={route.data ? `Route ${route.data.routeCode}` : 'Route detail'}
        description="Route header and stops timeline."
        actions={
          <Link to="/delivery/routes" className="px-3 py-2 text-sm font-medium text-nipost-green bg-nipost-green-light rounded-lg hover:bg-nipost-green hover:text-white">
            Back to routes
          </Link>
        }
      />

      {route.isPending ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : denied ? (
        <DeferredNotice title="Permission required">
          The dev token doesn&apos;t carry <code className="font-mono">Delivery/readRoute</code>.
        </DeferredNotice>
      ) : notFound ? (
        <DeferredNotice title="Not found">Route <code>{id}</code> is out of scope or does not exist.</DeferredNotice>
      ) : route.isError ? (
        <DeferredNotice title="Service unreachable">{(route.error as Error).message}</DeferredNotice>
      ) : route.data ? (
        <>
          <div className="bg-white border border-border rounded-xl p-5 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-xs text-muted">Status</p>
              <p className="mt-1"><StatusBadge status={route.data.status} /></p>
            </div>
            <div>
              <p className="text-xs text-muted">Driver</p>
              <p className="mt-1 font-mono text-xs">{route.data.driverId ?? '—'}</p>
            </div>
            <div>
              <p className="text-xs text-muted">Vehicle</p>
              <p className="mt-1 font-mono text-xs">{route.data.vehicleId ?? '—'}</p>
            </div>
            <div>
              <p className="text-xs text-muted">Scheduled start</p>
              <p className="mt-1 text-xs">{new Date(route.data.scheduledStart).toLocaleString()}</p>
            </div>
            <div>
              <p className="text-xs text-muted">Started at</p>
              <p className="mt-1 text-xs">{route.data.startedAt ? new Date(route.data.startedAt).toLocaleString() : '—'}</p>
            </div>
            <div>
              <p className="text-xs text-muted">Stops</p>
              <p className="mt-1 text-xs">{route.data.stopCount}</p>
            </div>
          </div>

          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Stops</h2>
            {(route.data.stops ?? []).length === 0 ? (
              <p className="text-sm text-muted bg-white border border-border rounded-xl p-8 text-center">No stops on this route.</p>
            ) : (
              <ol className="bg-white border border-border rounded-xl divide-y divide-border">
                {route.data.stops?.map((s) => (
                  <li key={s.id} className="px-5 py-3 flex items-start gap-4">
                    <span className="w-7 h-7 rounded-full bg-nipost-green-light text-nipost-green text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{s.sequence}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-sm font-medium text-gray-900 truncate">{s.kind} · {s.contactName ?? '—'}</p>
                        <StatusBadge status={s.status} />
                      </div>
                      <p className="text-xs text-muted mt-1">{s.contactPhone ?? '—'}</p>
                      <p className="text-xs text-gray-700 mt-1">
                        {s.arrivedAt ? `Arrived ${new Date(s.arrivedAt).toLocaleTimeString()}` : 'Not yet arrived'}
                        {s.completedAt ? ` · Completed ${new Date(s.completedAt).toLocaleTimeString()}` : ''}
                        {s.failureReason ? ` · ${s.failureReason}` : ''}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
